import type { JSX } from 'react';
import { useState } from 'react';
import { Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

interface CandidateConsentGateProps {
  onConsent: () => void;
}

export function CandidateConsentGate({ onConsent }: CandidateConsentGateProps): JSX.Element {
  const [dataConsent, setDataConsent] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-6 sm:px-6">
      <Card className="w-full max-w-xl gap-5">
        <CardHeader className="space-y-5 px-5 text-center sm:px-8">
          <div className="flex items-center justify-center gap-2">
            <Building2 aria-hidden="true" className="h-6 w-6 shrink-0 text-primary" />
            <span className="text-lg font-semibold">Career Verification</span>
          </div>
          <div className="space-y-3">
            <CardTitle>
              <h1 className="text-2xl leading-tight sm:text-3xl">Welcome to Career Verification</h1>
            </CardTitle>
            <CardDescription className="text-sm leading-relaxed sm:text-base">
              You&apos;ve been invited to create your professional profile. Add the work experience
              you want verified and provide contacts who can confirm it.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-5 px-5 sm:px-8">
          <section aria-labelledby="consent-heading" className="rounded-lg border p-4 sm:p-5">
            <h2 id="consent-heading" className="font-semibold">
              Before you continue
            </h2>
            <div className="mt-4 space-y-4">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="personal-data-consent"
                  checked={dataConsent}
                  onCheckedChange={(value) => setDataConsent(Boolean(value))}
                />
                <Label htmlFor="personal-data-consent" className="block leading-snug">
                  I consent to the processing of my personal data as described in the{' '}
                  <a
                    className="text-primary underline underline-offset-4"
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Notice
                  </a>
                  .
                </Label>
              </div>
              <div className="flex items-start gap-3">
                <Checkbox
                  id="age-confirmation"
                  checked={ageConfirmed}
                  onCheckedChange={(value) => setAgeConfirmed(Boolean(value))}
                />
                <Label htmlFor="age-confirmation" className="leading-snug">
                  I confirm that I am over the age of 16.
                </Label>
              </div>
            </div>
          </section>
          <Button className="w-full" disabled={!dataConsent || !ageConfirmed} onClick={onConsent}>
            Continue
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
